import type { FC } from 'react';
import { useRef } from 'react';

import BpkSelectableChip from '@skyscanner/backpack-web/bpk-component-chip';
import BpkMobileScrollContainer from '@skyscanner/backpack-web/bpk-component-mobile-scroll-container';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import PartnersFilter from '../PartnersFilter';

import type { FilterOptionOnClick } from '../../types';
import type { OfferFilter } from '@shared/types/frontend/ubff/hotel-detail-price';
import type { I18nService } from '@shared/types/i18n';
import type { ChipItem } from '@shared/ui/common/BpkChipGroup';

import STYLES from './OfferFiltersChips.module.scss';

import {
  BREAKPOINTS,
  withBpkBreakpoint,
} from '@shared/ui/common/BpkBreakpoint';
import { withI18n } from '@shared/ui/contexts/i18n';

const cls = cssModules(STYLES);

export type Props = {
  isMobile?: boolean;
  i18n: I18nService;
  chips: ChipItem[];
  partnersFilter?: OfferFilter;
  filterOptionOnClick: FilterOptionOnClick;
  openMultiplePrice?: boolean;
};

const OfferFiltersChips: FC<Props> = ({
  chips,
  filterOptionOnClick,
  i18n,
  isMobile,
  openMultiplePrice,
  partnersFilter,
}) => {
  const cacheRef = useRef<JSX.Element | null>(null);
  const partnerChips = partnersFilter?.options ?? [];
  const hasRateFilter = !!(partnerChips.length || chips.length);

  if (!openMultiplePrice && !hasRateFilter) {
    return null;
  }

  const rateFilters = (
    <BpkMobileScrollContainer>
      <div className={cls('OfferFiltersChips')}>
        {!isMobile && (
          <BpkText textStyle={TEXT_STYLES.sm}>
            {i18n.translate('Filters_label_filterBy')}
          </BpkText>
        )}
        {chips.map((chip) => (
          <BpkSelectableChip
            key={chip.text}
            selected={chip.selected}
            accessibilityLabel={chip.text}
            onClick={() => chip.onClick && chip.onClick(false, 0)}
            data-tracking-common-params={chip['data-tracking-common-params']}
            data-tracking-element-id={chip['data-tracking-element-id']}
          >
            {chip.text}
          </BpkSelectableChip>
        ))}
        {partnerChips.length > 0 && (
          <PartnersFilter
            partnerChips={partnerChips}
            isMobile={isMobile ?? false}
            partnersFilterTitle={partnersFilter?.title ?? ''}
            filterOptionOnClick={filterOptionOnClick}
          />
        )}
      </div>
    </BpkMobileScrollContainer>
  );

  if (hasRateFilter) {
    cacheRef.current = rateFilters;
  }

  return hasRateFilter ? rateFilters : cacheRef.current;
};

export default withI18n(
  withBpkBreakpoint(OfferFiltersChips, BREAKPOINTS.MOBILE, 'isMobile'),
);
